import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactPlayer from "react-player";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";

import { BoltPlayerState } from "../../types";
import { FlexRow } from "../FlexRow";
import { LoadingView } from "../LoadingView";
import PlayerControls from "./PlayerControls";
import StreamOffline from "../../assets/pngs/stream_offline.png";
import { createOtteraChannelTrackUrl } from "../../utils/formatter";
import { authSelector } from "../../store/auth/selector";

const AI_API_URL = process.env.REACT_APP_AI_API_URL;
const AZURE_KEY = process.env.REACT_APP_AZURE_SPEECH_KEY;
const AZURE_REGION = process.env.REACT_APP_AZURE_SPEECH_REGION;

type Props = {
  url?: string;
  channelId?: string;
  title?: string;
  isMuted?: boolean;
  isLive?: boolean;
  controls?: boolean;
  isPopup?: boolean;
  isSideChatBoxOpen?: boolean;
  isFloatingPlayer?: boolean;
  className?: string;
  handlePopout?: () => void;
  handleChatClose?: () => void;
  closeFloatingPlayer?: () => void;
  backToProfile?: () => void;
  handleVideoEnded?: () => void;
  isLoop?: boolean;
};

const BoltPlayer = ({
  url,
  channelId,
  title,
  isMuted = true,
  isLive = true,
  controls = true,
  isPopup = false,
  isSideChatBoxOpen,
  isFloatingPlayer = false,
  className = "",
  handlePopout,
  handleChatClose,
  closeFloatingPlayer,
  backToProfile,
  isLoop = true,
  handleVideoEnded,
}: Props) => {
  const playerRef = useRef<ReactPlayer>(null);
  const [loading, setLoading] = useState(isLive);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(isMuted);
  const [volume, setVolume] = useState(0.5);
  const [enablePip, setEnablePip] = useState(false);
  const [playerState, setPlayerState] = useState<BoltPlayerState>({
    played: 0,
    playedSeconds: 0,
    loaded: 0,
    loadedSeconds: 0,
  });

  // CC‐related state:
  type CcLang = "en" | "es" | "es-la" | "pt" | "pt-br" | "ar";
  const [ccLang, setCcLang] = useState<CcLang>("en");
  const [ccEnabled, setCcEnabled] = useState(false);
  const [caption, setCaption] = useState<{
    text: string;
    interim: boolean;
  } | null>(null);
  const textTrackRef = useRef<TextTrack | null>(null);
  const offsetRef = useRef<number | null>(null); // alignment between Azure offset and video currentTime

  /* ------------- new refs so we reuse AudioContext & source ------------- */
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const { user: { id: currentUserId = "" } = {} } = useSelector(authSelector);
  const channelFromPath = useMemo(() => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  }, []);

  const TRACK_LABEL: Record<CcLang, string> = {
    en: "Live CC (English)",
    es: "Live CC (Spanish – ES)",
    "es-la": "Live CC (Spanish – LatAm)",
    pt: "Live CC (Portuguese – PT)",
    "pt-br": "Live CC (Portuguese – Brasil)",
    ar: "Live CC (Arabic)",
  };
  
  const TRACK_LANG_BCP47: Record<CcLang, string> = {
    en: "en",
    es: "es-ES",
    "es-la": "es-419",   // Pan-regional Spanish (LatAm)
    pt: "pt-PT",
    "pt-br": "pt-BR",
    ar: "ar-SA",
  };
  
  const trackMapRef = useRef<Record<CcLang, TextTrack | null>>({
    en: null,
    es: null,
    "es-la": null,
    pt: null,
    "pt-br": null,
    ar: null,
  });

  const SOURCE_LANGUAGE_CANDIDATES = ["en-US", "es-ES", "pt-BR", "ar-SA"];
  const DEFAULT_SOURCE_LANGUAGE = SOURCE_LANGUAGE_CANDIDATES[0];

  // Utility: get or create a <track> on the video element
  const getOrCreateTrack = (): TextTrack => {
    const videoEl = playerRef.current?.getInternalPlayer() as HTMLVideoElement | null;
    if (!videoEl) throw new Error("video element not ready");
  
    if (!trackMapRef.current[ccLang]) {
      const t = videoEl.addTextTrack("captions", TRACK_LABEL[ccLang], TRACK_LANG_BCP47[ccLang]);
      t.mode = "disabled"; // we render overlay
      trackMapRef.current[ccLang] = t;
    }
    return trackMapRef.current[ccLang]!;
  };
  

  // Remove a cue by its ID if it already exists
  function removeCueIfExists(track: TextTrack, id: string) {
    Array.from(track.cues ?? []).forEach((c) => {
      if (c.id === id) track.removeCue(c);
    });
  }

  // When CC flips on/off or url changes, set up or tear down Azure‐speech‐based captions
  useEffect(() => {
    if (!ccEnabled || !url) {
      return () => {}; // always return a cleanup fn per ESLint consistent-return
    }
    if (!AZURE_KEY || !AZURE_REGION) {
      console.error("[CC] Missing Azure key / region → captions disabled");
      setCcEnabled(false);
      return () => {};
    }

    /* ---------- 1. Web-audio plumbing ---------- */
    const videoEl = playerRef.current!.getInternalPlayer() as HTMLVideoElement;
    /* one AudioContext for the whole component */
    let audioCtx = audioCtxRef.current;
    if (!audioCtx) {
      audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
    }

    /* one MediaElementAudioSourceNode for the <video>, created once */
    let source = sourceRef.current;
    if (!source) {
      source = audioCtx.createMediaElementSource(videoEl);
      source.connect(audioCtx.destination); /* permanent speaker path */
      sourceRef.current = source;
    }

    /* fresh ScriptProcessor tap each time CC is turned on */
    const script = audioCtx.createScriptProcessor(4096, 1, 1);
    source.connect(script);
    script.connect(audioCtx.destination);

    /* wake the context on first user gesture */
    if (audioCtx.state === "suspended") {
      audioCtx.resume().catch((e) => console.warn("[CC] resume err", e));
    }

    /* ---------- 2. Azure Push stream ---------- */
    const format = SpeechSDK.AudioStreamFormat.getWaveFormatPCM(
      audioCtx.sampleRate /* eg 44100 or 48000 */,
      16 /* bitsPerSample */,
      1 /* channels    */
    );
    const pushStream = SpeechSDK.AudioInputStream.createPushStream(format);

    let totalPushedBytes = 0;
    script.onaudioprocess = (ev) => {
      const input = ev.inputBuffer.getChannelData(0);
      const int16 = new Int16Array(input.length);
      for (let i = 0; i < input.length; i += 1) {
        const s = Math.max(-1, Math.min(1, input[i]));
        int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      pushStream.write(int16.buffer);
      totalPushedBytes += int16.byteLength;
    };

    /* ---------- 3. Azure recogniser ---------- */
    /* ---------- 3. Azure recogniser ---------- */
    const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(pushStream);

    type TargetMap = Record<CcLang, string>;
    const targetLangCodes: TargetMap = {
      en: "en",
      es: "es",
      "es-la": "es", // Azure Translation: use generic Spanish
      pt: "pt",
      "pt-br": "pt", // Azure Translation: use generic Portuguese
      ar: "ar",
    };
    const targetLangKey = targetLangCodes[ccLang] ?? "en";

    const autoDetectConfig = SpeechSDK.AutoDetectSourceLanguageConfig.fromLanguages(
      SOURCE_LANGUAGE_CANDIDATES
    );

    const tConfig = SpeechSDK.SpeechTranslationConfig.fromSubscription(AZURE_KEY, AZURE_REGION);
    tConfig.speechRecognitionLanguage = DEFAULT_SOURCE_LANGUAGE;
    tConfig.addTargetLanguage(targetLangKey);
    tConfig.setProperty("SpeechServiceConnection_SingleLanguageIdPriority", "Latency");
    const recognizer = SpeechSDK.TranslationRecognizer.FromConfig(
      tConfig,
      autoDetectConfig,
      audioConfig
    );

    /* ----- event wiring (works for both recognizers) ----- */
    let firstFinalSeen = false;

    // Interim
    (recognizer as any).recognizing = (_s: any, e: any) => {
      const reason = e.result?.reason;
      if (reason !== SpeechSDK.ResultReason.TranslatingSpeech) return;

      let text = e.result?.translations?.get(targetLangKey) || "";
      if (!text && targetLangKey === "en") {
        text = e.result?.text || "";
      }

      if (text) setCaption({ text, interim: true });
    };

    // Final
    (recognizer as any).recognized = (_s: any, e: any) => {
      // Reason guards differ slightly:
      const reason = e.result?.reason;
      if (reason !== SpeechSDK.ResultReason.TranslatedSpeech) return;

      // Offsets/durations exist for both result types:
      const offsetS = e.result.offset / 10_000_000;
      const durS = e.result.duration / 10_000_000;

      if (!firstFinalSeen) {
        const videoNow = playerRef.current!.getCurrentTime();
        offsetRef.current = videoNow - offsetS;
        firstFinalSeen = true;
      }

      const start = offsetS + (offsetRef.current ?? 0);
      const end = start + durS;
      const track = getOrCreateTrack();

      removeCueIfExists(track, "azure-interim");
      const cueId = `azure-${start.toFixed(2)}`;
      removeCueIfExists(track, cueId);

      let text = "";
      text = e.result?.translations?.get(targetLangKey) || "";
      if (!text && targetLangKey === "en") {
        text = e.result?.text || "";
      }
      if (!text) return;

      const cue = new VTTCue(start, end, text);
      cue.id = cueId;
      track.addCue(cue);
      // Optional: clear overlay because the cue will show on your overlay anyway
      // setCaption({ text, interim: false });
    };

    (recognizer as any).canceled = (_s: any, e: any) => {
      console.warn("[CC] recognizer canceled:", e.errorDetails);
      setCcEnabled(false);
    };

    (recognizer as any).sessionStarted = () => console.log("[CC] Azure session started");

    (recognizer as any).startContinuousRecognitionAsync(
      () => console.log("[CC] recognizer started"),
      (err: any) => {
        console.error("[CC] start error:", err);
        setCcEnabled(false);
      }
    );


    /* ---------- cleanup ---------- */
    return () => {
      console.log("[CC] cleaning up, total bytes pushed:", totalPushedBytes);

      // 1) Detach ONLY the tap (source <-> script)
      if (sourceRef.current) {
        // disconnect may throw in some browsers if already disconnected, but in practice it's fine.
        sourceRef.current.disconnect(script);
      }
      script.disconnect();
      script.onaudioprocess = null;

      // 2) Stop recognizer, then dispose + close push stream
      recognizer.stopContinuousRecognitionAsync(
        () => {
          if (typeof (pushStream as any).close === "function") {
            (pushStream as any).close();
          }
          (recognizer as any).close?.();
          console.log("[CC] recognizer stopped");
        },
        (err) => console.warn("[CC] stop error:", err)
      );


      // 3) Wipe cues that THIS run produced.
      // Capture the language used during this effect run:
      const langForThisRun = ccLang;
      const track = trackMapRef.current[langForThisRun];
      if (track) {
        Array.from(track.cues ?? []).forEach((c) => track.removeCue(c));
      }

      // If you prefer to wipe ALL languages on teardown, use this instead:
      // Object.values(trackMapRef.current).forEach((t) => {
      //   if (t) Array.from(t.cues ?? []).forEach((c) => t.removeCue(c));
      // });

      offsetRef.current = null;
      setCaption(null);
    };
  }, [ccEnabled, url, ccLang]);
  // Whenever the URL changes, resume playback
  useEffect(() => {
    setPlaying(true);
  }, [url]);

  const toggleMute = () => {
    if (volume === 0) {
      setMuted(false);
      setVolume(0.5);
    } else {
      setMuted(!muted);
    }
  };

  const playerValue = playerRef.current?.getInternalPlayer("hls");

  const handleCcToggle = () => {
    // If we’re about to enable CC, make sure there is at least a little audio
    if (!ccEnabled && (muted || volume === 0)) {
      setMuted(false);
      setVolume(0.1);
    }

    /* ---------------- analytics ping ---------------- */
    const turningOn = !ccEnabled;           // about to enable?
    fetch(`${AI_API_URL}/cc-event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        channel: channelFromPath,
        user_id: currentUserId,
        enabled: turningOn,
      }),
    }).catch(() => { /* fire-and-forget */ });

    setCcEnabled((v) => !v);
  };

  const handleCcLangChange = (lang: CcLang) => {
    if (!ccEnabled) {
      handleCcToggle();
    }
    setCcLang(lang);
  };

  return (
    <>
      {/* Inject CSS for WebKit caption styling */}
      <style>{`
        video::-webkit-media-text-track-display {
          display: none !important;
        }

        video::cue {
          display: none !important;
        }
      `}</style>

      <FlexRow
        component={Box}
        sx={{
          flex: 1,
          maxHeight: isPopup ? "100vh" : "90vh",
          minHeight: isPopup ? "100vh" : "100%",
          minWidth: "100%",
          height: "auto",
          width: "100%",
          overflow: "hidden",
          position: "relative",
          bgcolor: "black",
          transition: "all 0.2s ease-out",
        }}
      >
        {isLive ? (
          <>
            <ReactPlayer
              ref={playerRef}
              className={className}
              style={{ display: "flex", marginTop: -6, transition: "all 0.8s ease" }}
              height="auto"
              width="100%"
              playsinline
              muted={muted}
              playing={playing}
              pip={enablePip}
              volume={volume}
              url={url!}
              loop={isLoop}
              onEnded={() => {
                if (!isLoop && handleVideoEnded) {
                  handleVideoEnded();
                }
              }}
              onDisablePIP={() => setEnablePip(false)}
              onReady={() => setLoading(false)}
              onPlay={() => setPlaying(true)}
              onBuffer={() => setLoading(true)}
              onBufferEnd={() => setLoading(false)}
              onError={() => setLoading(false)}
              onProgress={setPlayerState}
              config={{
                file: {
                  hlsOptions: {
                    xhrSetup: function xhrSetup(xhr: any, url: string) {
                      const newUrl = createOtteraChannelTrackUrl(url, {
                        channelId,
                        channelName: title,
                      });
                      xhr.open("GET", newUrl);
                    },
                  },
                  attributes: {
                    controlsList: "nodownload",
                    id: "player",
                  },
                },
              }}
            />
            {playerRef.current && controls && isLive && (
              <PlayerControls
                isChatOpen={isSideChatBoxOpen}
                isPopup={isPopup}
                title={title!}
                description={title!}
                playing={playing}
                muted={muted as boolean}
                volume={volume}
                setPlaying={setPlaying}
                onChangeVolume={setVolume}
                togglePip={() => setEnablePip(true)}
                setMuted={setMuted}
                toggleMute={toggleMute}
                state={playerState}
                player={playerRef.current}
                type={isLive ? "live" : "normal"}
                handlePopout={handlePopout}
                handleChatClose={handleChatClose}
                isFloatingVideoPlayer={isFloatingPlayer}
                closeFloatingPlayer={closeFloatingPlayer}
                backToProfile={backToProfile}
                playerValue={playerValue}
                ccEnabled={ccEnabled}
                ccLang={ccLang}
                onToggleCc={handleCcToggle}
                onChangeCcLang={handleCcLangChange}
              />
            )}

            {/* —— our CC button —— */}
            {/* <div style={{ position: "relative" }}>
              <button
                type="button"
                onClick={handleCcToggle}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  zIndex: 999,

                  // Button styling
                  background: ccEnabled ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.1)",
                  color: ccEnabled ? "#000" : "#fff",
                  border: ccEnabled ? "2px solid #fff" : "2px solid rgba(255, 255, 255, 0.3)",

                  // Size and spacing
                  padding: "4px 10px",
                  fontSize: "12px",
                  fontWeight: 600,
                  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                  letterSpacing: "0.5px",

                  // Shape
                  borderRadius: "4px",

                  // Interactions
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (!ccEnabled) {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                    e.currentTarget.style.border = "2px solid rgba(255, 255, 255, 0.6)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!ccEnabled) {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                    e.currentTarget.style.border = "2px solid rgba(255, 255, 255, 0.3)";
                  }
                }}
              >
                Live CC
                <div
                  style={{
                    position: "absolute",
                    top: -5,
                    right: -15,
                    background: "#ff3b30",
                    color: "white",
                    fontSize: "8px",
                    padding: "2px 8px",
                    transform: "rotate(45deg)",
                    transformOrigin: "center",
                    fontWeight: "bold",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    letterSpacing: "0.5px",
                    borderRadius: "2px",
                    animation: "pulse 2s infinite",
                  }}
                >
                  NEW
                </div>
              </button>
              <select
                value={ccLang}
                onChange={(e) => setCcLang(e.target.value as CcLang)}
                style={{
                  position: "absolute",
                  top: 10, right: 90, zIndex: 999,
                  background: "rgba(0,0,0,0.5)", color: "#fff",
                  border: "1px solid rgba(255,255,255,0.3)",
                  padding: "4px 6px", borderRadius: 4
                }}
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="pt">Português</option>
              </select>
              <style>
                {`
                  @keyframes pulse {
                    0% { transform: rotate(45deg) scale(1); }
                    50% { transform: rotate(45deg) scale(1.1); }
                    100% { transform: rotate(45deg) scale(1); }
                  }
                `}
              </style>
            </div> */}

            {/* -- caption overlay -- */}
            {ccEnabled && caption && (
              <div
                style={{
                  position: "absolute",
                  bottom: "2px",
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  pointerEvents: "none",
                  zIndex: 4,
                  padding: "0 5%",

                  maxHeight: "16.6667%",
                  overflow: "hidden",

                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <span
                  className="caption_text_style"
                  style={{
                    fontSize: "1.5rem",
                    color: "#fff",
                    backgroundColor: "#000",
                    fontFamily: "Arial, \"Century Gothic\", Verdana, Roboto, Tahoma, sans-serif",
                    fontWeight: 400,
                    fontStyle: "normal",
                    lineHeight: 1.3,
                    display: "inline",
                    width: "auto",
                    maxWidth: "100%",
                    wordBreak: "break-word",
                    padding: "0.12em 0.35em",
                    boxDecorationBreak: "clone",
                    WebkitBoxDecorationBreak: "clone",
                    opacity: caption.interim ? 0.8 : 1,
                    transition: "opacity 0.1s ease",
                  }}
                >
                  {caption.text}
                </span>
              </div>
            )}
          </>
        ) : (
          <img
            src={StreamOffline}
            alt="Stream Offline"
            height="auto"
            width="100%"
            style={{ display: "flex", marginTop: -6 }}
          />
        )}

        <LoadingView
          visible={(loading && isLive) || (ccEnabled && false) /* no separate loading state needed */}
          sx={{ position: "absolute", zIndex: 2 }}
        />
      </FlexRow>
    </>
  );
};

export default React.memo(BoltPlayer);
